import React, { useState, useContext, useMemo } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import { useTheme } from "../../context/ThemeContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import { parseError, getAuthService } from "@ntconnect/framework/pbx";
import NtModalError from "../../components/NtModalError";
import NtModalInputField from "../../components/NtModalInputField";
import { updateUser } from "../../api/user";
import Logger from "../../common/utils/Logger";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import {
  isValidEmail,
  isValidPassword,
  MIN_PASSWORD_LENGTH,
} from "../../onboarding/utils/validationUtils";

// Edit-profile form, shared by Settings (self) and User Detail (admin editing
// another user). Profile fields (name/email) go through the standard user
// PATCH. The password, however, is NOT sent through that PATCH anymore — under
// the BFF auth model it bcrypt-hashes nothing and would store plaintext. Both
// password paths instead go through the auth service:
//   - self  → changePassword(domain, current, new)  (verifies current pw)
//   - admin → recoverUserPassword(domain, userId, new)  (platform creds, no current)
function NtUserUpdateForm({ item, onCancel, onUpdate }) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const context = useContext(UserContext);

  const [name, setName] = useState(item.contact?.contact_name_given || "");
  const [email, setEmail] = useState(item?.user_email || "");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Two password paths share this form:
  //  - Self-service (editing your own account): changePassword() verifies the
  //    current password and targets the signed-in user (UUID from the JWT).
  //  - Admin reset (editing someone else): recoverUserPassword() sets the
  //    target user's password with no current-password check (platform creds).
  // We pick by comparing the edited record to the signed-in user.
  const isSelf = item?.user_uuid && item.user_uuid === context.user?.userId;

  // The password section is "engaged" the moment the user touches any of its
  // fields. Until then it's left alone and the profile saves on its own.
  const changingPassword = !!(currentPassword || newPassword || confirmPassword);

  const profileValid = !!name?.trim() && isValidEmail(email);
  const canSubmit = profileValid && !loading;

  const validatePasswordChange = () => {
    // Current password is only meaningful (and required) for self-service.
    if (isSelf && !currentPassword) return "Enter your current password.";
    if (!isValidPassword(newPassword))
      return `New password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    if (newPassword !== confirmPassword)
      return "New password and confirmation do not match.";
    return null;
  };

  const handleUpdate = async () => {
    if (!profileValid) {
      setError("Enter a name and a valid email address.");
      return;
    }

    if (changingPassword) {
      const pwError = validatePasswordChange();
      if (pwError) {
        setError(pwError);
        return;
      }
    }

    setLoading(true);
    setError(null);

    // 1) Password first — a wrong current password should fail loudly before
    //    we touch anything else, and it must never silently no-op.
    if (changingPassword) {
      try {
        if (isSelf) {
          await getAuthService().changePassword(
            context.user?.domain,
            currentPassword,
            newPassword,
          );
          Logger("Password changed for current user");
        } else {
          await getAuthService().recoverUserPassword(
            context.user?.domain,
            item.user_uuid,
            newPassword,
          );
          Logger("Password reset for user", item.user_uuid);
        }
      } catch (err) {
        Logger("password change failed", err);
        setError(err?.message || "Could not change the password.");
        setLoading(false);
        return;
      }
    }

    // 2) Profile fields — note: password is intentionally NOT included here.
    const response = await updateUser(context.user?.domain, item.user_uuid, {
      name,
      email,
    });
    Logger("-------- response for update", response);

    if (response.ok) {
      onUpdate?.();
    } else {
      setError(parseError(response));
    }

    setLoading(false);
  };

  return (
    <View style={styles.container}>
      <NtModalTitle>Edit profile</NtModalTitle>
      <NtModalSubtitle style={{ marginTop: 4 }}>
        Update your account details and password.
      </NtModalSubtitle>

      <NtText style={styles.sectionLabel}>PROFILE</NtText>

      <NtModalInputField
        containerStyle={{ marginTop: 10 }}
        title={"Name *"}
        placeholder={"Name"}
        value={name}
        setValue={setName}
      />

      <NtModalInputField
        containerStyle={{ marginTop: 12 }}
        title={"Email *"}
        placeholder={"name@company.com"}
        keyboardType="email-address"
        value={email}
        setValue={setEmail}
      />

      <View style={styles.divider} />

      <NtText style={styles.sectionLabel}>
        {isSelf ? "CHANGE PASSWORD" : "RESET PASSWORD"}
      </NtText>
      <NtText style={styles.sectionHint}>
        {isSelf
          ? "Leave these blank to keep your current password."
          : "Set a new password for this user. Leave blank to keep their current one."}
      </NtText>

      {isSelf ? (
        <NtModalInputField
          containerStyle={{ marginTop: 10 }}
          title={"Current password"}
          placeholder={"Current password"}
          secureTextEntry
          showEye
          value={currentPassword}
          setValue={setCurrentPassword}
        />
      ) : null}

      <NtModalInputField
        containerStyle={{ marginTop: isSelf ? 12 : 10 }}
        title={"New password"}
        placeholder={"New password"}
        footer={`At least ${MIN_PASSWORD_LENGTH} characters.`}
        secureTextEntry
        showEye
        value={newPassword}
        setValue={setNewPassword}
      />

      <NtModalInputField
        containerStyle={{ marginTop: 12 }}
        title={"Confirm new password"}
        placeholder={"Confirm new password"}
        secureTextEntry
        showEye
        value={confirmPassword}
        setValue={setConfirmPassword}
      />

      <NtModalError error={error} containerStyle={{ marginTop: 10 }} />

      <View style={styles.footer}>
        {loading && (
          <NtActivityIndicator
            color={theme.brand2}
            size="small"
            containerStyle={{ marginRight: 20 }}
          />
        )}
        <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />
        <NtModalActiveButton
          containerStyle={{ marginLeft: 15 }}
          title={changingPassword ? "Save changes" : "Update"}
          onPress={() => {
            if (canSubmit) handleUpdate();
          }}
          enabled={canSubmit}
        />
      </View>
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    container: {
      flex: 1,
      minWidth: 500,
      maxWidth: 800,
      padding: 15,
    },
    sectionLabel: {
      color: t.textMute,
      fontSize: 11,
      fontWeight: "700",
      letterSpacing: 0.5,
      marginTop: 22,
    },
    sectionHint: {
      color: t.textDim,
      fontSize: 12,
      marginTop: 2,
    },
    divider: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: t.borderSoft,
      marginTop: 22,
    },
    footer: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-end",
      marginTop: 25,
    },
  });

export default NtUserUpdateForm;
